import { useRef, useState, type ReactNode } from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { PopMenu } from '../PopMenu';

// THE FRAME EVERY SCREEN BUT THE PLAYER STANDS IN (splash, tools, parts, success), so that moving
// from one to the next nothing jumps: the content starts at the same height, under a header band
// of the same height whether it holds anything or not, and the one black button sits at the same
// place, at the very bottom (Louis 2026-10-23). A screen with no header (the splash, the
// success) has no band either: its title goes up to the language toggle's line, level with it
// (12px down, the toggle's own top), where the band was empty room. Tight at both ends, a phone's height being what
// it is. The middle alone scrolls, and only where it must (the parts list): a screen with room to
// share, the splash, fills it instead (`h-full` on its own column).
export function PublicScreen({ header, children, primary }: {
  header?: ReactNode;
  children: ReactNode;
  primary: { label: string; onClick: () => void };
}) {
  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-white font-satoshi">
      <div className={`mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col px-5 pb-4 ${header ? 'pt-2' : 'pt-3'}`}>
        {header && <div className="mb-2 flex h-10 shrink-0 items-center justify-center">{header}</div>}
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        {/* A clear gap over the button, so what is above it plainly ends there. */}
        <button
          type="button"
          onClick={primary.onClick}
          className="mt-6 w-full shrink-0 rounded-full bg-notice-ink py-4 text-base font-bold text-white shadow-sm transition-opacity hover:opacity-90"
        >
          {primary.label}
        </button>
      </div>
    </div>
  );
}

// WHERE THIS SCREEN IS: back on the left, the count and its bar in the middle ("01 / 06"). The
// tools and parts screens' header, and the steps' (Louis 2026-10-23), which counted alone. The
// count padded to the total's width, two digits at least, so it keeps its length as it runs.
// `onPick`: the count opens a menu of every screen, to go straight to one (the steps', Louis
// 2026-10-26, to reach the last steps on a phone without walking the ones before).
export function ScreenProgress({ at, of, onBack, onPick }: {
  at: number; of: number; onBack: () => void; onPick?: (n: number) => void;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const count = useRef<HTMLButtonElement>(null);
  const pad = (n: number) => String(n).padStart(Math.max(2, String(of).length), '0');
  return (
    <div className="relative flex w-full items-center justify-center">
      <button
        type="button"
        onClick={onBack}
        aria-label={t('notice.public.back')}
        className="absolute left-0 rounded-full p-1.5 text-notice-blue transition-colors hover:bg-notice-blue/5"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>
      <div className="flex flex-col items-center gap-1.5">
        <button
          ref={count}
          type="button"
          disabled={!onPick}
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="text-sm font-bold tabular-nums text-notice-ink disabled:cursor-default"
        >
          {`${pad(at)} / ${pad(of)}`}
        </button>
        <div className="h-1 w-36 overflow-hidden rounded-full bg-notice-blue/15">
          <div className="h-full rounded-full bg-notice-blue" style={{ width: `${(at / of) * 100}%` }} />
        </div>
      </div>
      {open && onPick && (
        <PopMenu anchor={count} onClose={() => setOpen(false)} center>
          {Array.from({ length: of }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => {
                setOpen(false);
                onPick(n);
              }}
              // The label centred, the tick beside it out of the way so it does not push it off. A
              // fixed width: `w-full` in the menu's `w-max` made iOS lay each row 1280 px wide.
              className="relative block w-36 px-8 py-2.5 text-center font-satoshi text-sm text-slate-800 transition-colors hover:bg-slate-50"
            >
              {`${t('notice.step')} ${n}`}
              {n === at && <Check className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-notice-blue" />}
            </button>
          ))}
        </PopMenu>
      )}
    </div>
  );
}
